import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { BASE_URL, API_URLS } from '../constants/config';
import { checkIfTokenExpired, getTokensFromStore } from './tokens';
import { refreshTokens } from '../actions/usersActions';
import store from './store';
import { useAuth } from './auth';
import { champFields } from './tournamentsApi';
import {
  EMPTY_PENDING_CAP_APPLICATIONS,
  extractPendingCapApplications,
  pendingCapCountForChamp,
} from './applicationsApi';
import {
  EMPTY_PENDING_CLIENT_FORMS,
  extractPendingClientForms,
  pendingClientFormsCountForChamp,
} from './clientFormsApi';

export const CHAMP_STORAGE_KEY = 'afl_admin_champ_id';

export const parseChampId = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
};

export const getStoredChampId = () => parseChampId(localStorage.getItem(CHAMP_STORAGE_KEY));

export const setStoredChampId = (id) => {
  const parsed = parseChampId(id);
  if (parsed) {
    localStorage.setItem(CHAMP_STORAGE_KEY, String(parsed));
    return parsed;
  }
  localStorage.removeItem(CHAMP_STORAGE_KEY);
  return null;
};

export const getDefaultChampIdFromUser = (userData) => parseChampId(
  userData?.default_champ_id
    ?? userData?.defaultChampId
    ?? userData?.data?.default_champ_id
    ?? userData?.data?.defaultChampId
);

export const applyLoginDefaultChampId = (userData) => {
  const parsed = getDefaultChampIdFromUser(userData);
  if (parsed) {
    setStoredChampId(parsed);
  } else {
    localStorage.removeItem(CHAMP_STORAGE_KEY);
  }
  return parsed;
};

export const flattenChampionships = (groups = []) => {
  const list = [];
  groups.forEach((city) => {
    (city.championships || []).forEach((championship) => {
      const id = parseChampId(championship?.id);
      if (!id) return;
      list.push({
        ...championship,
        id,
        city_id: city.city_id,
        city: city.city,
        city_ru: city.city_ru,
        city_flag: city.flag || city.city_flag,
        flag: city.flag || city.city_flag,
      });
    });
  });
  return list;
};

const normalizeGroups = (data) => {
  const groups = Array.isArray(data) ? data : [];
  return [...groups]
    .sort((a, b) => (Number(a.city_priority) || 0) - (Number(b.city_priority) || 0))
    .map((city) => ({
      ...city,
      championships: [...(city.championships || [])]
        .sort((a, b) => (Number(a.priority) || 0) - (Number(b.priority) || 0))
        .map((championship) => ({
          ...championship,
          id: parseChampId(championship?.id),
        }))
        .filter((championship) => championship.id),
    }))
    .filter((city) => city.championships.length > 0);
};

const getAccessToken = (userData) => (
  getTokensFromStore()?.access_token
    || userData?.access_token
    || userData?.data?.access_token
    || null
);

const fetchGroupedChampionships = async (userData) => {
  const reduxTokens = getTokensFromStore();
  if (reduxTokens?.access_token && checkIfTokenExpired()) {
    try {
      await store.dispatch(refreshTokens());
    } catch (error) {
      // keep the current token and try the request anyway
    }
  }

  const accessToken = getAccessToken(userData);
  if (!accessToken) {
    const error = new Error('Пользователь не авторизован');
    error.response = { status: 401, data: 'Пользователь не авторизован' };
    throw error;
  }

  const response = await axios.get(BASE_URL.url + API_URLS.getChampionshipsGroupedByCity, {
    headers: {
      Authorization: 'Bearer ' + accessToken,
      Accept: 'application/json',
    },
  });

  return Array.isArray(response.data) ? response.data : response.data?.data || [];
};

const resolveChampId = (flat, userData) => {
  const stored = getStoredChampId();
  const loginDefault = getDefaultChampIdFromUser(userData);
  if (stored && flat.some((item) => item.id === stored)) return stored;
  if (loginDefault && flat.some((item) => item.id === loginDefault)) return loginDefault;
  return flat[0]?.id || null;
};

/** Poll interval for sidebar badges while admin session is open. */
export const ADMIN_BADGES_POLL_MS = 90_000;
/** Skip soft focus/visibility refreshes closer than this. */
const ADMIN_BADGES_SOFT_GAP_MS = 8_000;

const ChampionshipContext = createContext({
  groups: [],
  championships: [],
  champId: null,
  selected: null,
  loading: false,
  error: null,
  pendingCapApplicationsCount: 0,
  pendingClientFormsCount: 0,
  setChampId: () => {},
  reload: () => {},
  refreshAdminBadges: async () => {},
  refreshPendingCapApplications: async () => {},
  refreshPendingClientForms: async () => {},
});

const postWithAuth = async (userData, url, body) => {
  const reduxTokens = getTokensFromStore();
  if (reduxTokens?.access_token && checkIfTokenExpired()) {
    try {
      await store.dispatch(refreshTokens());
    } catch (error) {
      // keep the current token and try the request anyway
    }
  }

  const accessToken = getAccessToken(userData);
  if (!accessToken) {
    const error = new Error('Пользователь не авторизован');
    error.response = { status: 401, data: 'Пользователь не авторизован' };
    throw error;
  }

  const response = await axios.post(
    BASE_URL.url + url,
    body,
    {
      headers: {
        Authorization: 'Bearer ' + accessToken,
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
    }
  );

  return response.data;
};

const persistDefaultChamp = async (userData, champId) => {
  const parsed = parseChampId(champId);
  if (!parsed) return null;
  return postWithAuth(userData, API_URLS.setDefaultChamp, { default_champ_id: parsed });
};

export function ChampionshipProvider({ children }) {
  const { userData, setUserData } = useAuth();
  const [groups, setGroups] = useState([]);
  const [champId, setChampIdState] = useState(getStoredChampId);
  const [pendingCap, setPendingCap] = useState(EMPTY_PENDING_CAP_APPLICATIONS);
  const [pendingClientForms, setPendingClientForms] = useState(EMPTY_PENDING_CLIENT_FORMS);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const loadedForUserRef = useRef(null);
  const pendingSeededForRef = useRef(null);
  const badgesInFlightRef = useRef(null);
  const lastBadgesAtRef = useRef(0);
  const champIdRef = useRef(champId);
  champIdRef.current = champId;
  const userDataRef = useRef(userData);
  userDataRef.current = userData;
  const [loadNonce, setLoadNonce] = useState(0);

  const applyPendingPayload = useCallback((payload) => {
    const next = extractPendingCapApplications(payload);
    if (!next) return null;
    setPendingCap(next);
    return next;
  }, []);

  const applyPendingClientFormsPayload = useCallback((payload) => {
    const next = extractPendingClientForms(payload);
    if (!next) return null;
    setPendingClientForms(next);
    return next;
  }, []);

  const syncUserDefaultChamp = useCallback((id, pending, pendingForms, accessPatch) => {
    const parsed = parseChampId(id);
    const currentUser = userDataRef.current;
    if (!setUserData || !currentUser) return;
    const nextUser = { ...currentUser };
    let changed = false;
    if (parsed && getDefaultChampIdFromUser(currentUser) !== parsed) {
      nextUser.default_champ_id = parsed;
      changed = true;
    }
    if (pending) {
      const sameCap = currentUser.pending_cap_applications === pending.pending_cap_applications
        && JSON.stringify(currentUser.pending_cap_applications_by_champ || [])
          === JSON.stringify(pending.pending_cap_applications_by_champ || []);
      if (!sameCap) {
        nextUser.pending_cap_applications = pending.pending_cap_applications;
        nextUser.pending_cap_applications_by_champ = pending.pending_cap_applications_by_champ;
        changed = true;
      }
    }
    if (pendingForms) {
      const sameForms = currentUser.pending_client_forms === pendingForms.pending_client_forms
        && JSON.stringify(currentUser.pending_client_forms_by_champ || [])
          === JSON.stringify(pendingForms.pending_client_forms_by_champ || []);
      if (!sameForms) {
        nextUser.pending_client_forms = pendingForms.pending_client_forms;
        nextUser.pending_client_forms_by_champ = pendingForms.pending_client_forms_by_champ;
        changed = true;
      }
    }
    // Роли / admin_access с getAdminBadges — без перелогина
    if (accessPatch && typeof accessPatch === 'object') {
      if (Array.isArray(accessPatch.roles)) {
        const sameRoles = JSON.stringify(currentUser.roles || []) === JSON.stringify(accessPatch.roles);
        if (!sameRoles) {
          nextUser.roles = accessPatch.roles;
          changed = true;
        }
      }
      if (accessPatch.role != null && Number(currentUser.role) !== Number(accessPatch.role)) {
        nextUser.role = accessPatch.role;
        changed = true;
      }
      if (Array.isArray(accessPatch.champs_ids)) {
        const sameChamps = JSON.stringify(currentUser.champs_ids || [])
          === JSON.stringify(accessPatch.champs_ids);
        if (!sameChamps) {
          nextUser.champs_ids = accessPatch.champs_ids;
          changed = true;
        }
      }
      if (typeof accessPatch.unrestricted_champs === 'boolean'
        && Boolean(currentUser.unrestricted_champs) !== accessPatch.unrestricted_champs) {
        nextUser.unrestricted_champs = accessPatch.unrestricted_champs;
        changed = true;
      }
      if (accessPatch.admin_access && typeof accessPatch.admin_access === 'object') {
        const sameAccess = JSON.stringify(currentUser.admin_access || {})
          === JSON.stringify(accessPatch.admin_access);
        if (!sameAccess) {
          nextUser.admin_access = accessPatch.admin_access;
          changed = true;
        }
      }
      if (Array.isArray(accessPatch.champ_access)) {
        const sameChampAccess = JSON.stringify(currentUser.champ_access || [])
          === JSON.stringify(accessPatch.champ_access);
        if (!sameChampAccess) {
          nextUser.champ_access = accessPatch.champ_access;
          changed = true;
        }
      }
    }
    if (changed) setUserData(nextUser);
  }, [setUserData]);

  const setChampId = useCallback((id, options = {}) => {
    const next = setStoredChampId(id);
    setChampIdState(next);
    if (!options.persist || !next) return next;
    if (getDefaultChampIdFromUser(userData) === next) return next;

    persistDefaultChamp(userData, next)
      .then((data) => {
        const pending = applyPendingPayload(data);
        const pendingForms = applyPendingClientFormsPayload(data);
        syncUserDefaultChamp(next, pending, pendingForms);
      })
      .catch(() => {});
    return next;
  }, [applyPendingPayload, applyPendingClientFormsPayload, syncUserDefaultChamp, userData]);

  const refreshAdminBadges = useCallback(async (options = {}) => {
    const force = Boolean(options.force);
    const currentUser = userDataRef.current;
    if (!currentUser) return null;

    const now = Date.now();
    if (!force && now - lastBadgesAtRef.current < ADMIN_BADGES_SOFT_GAP_MS) {
      return null;
    }
    if (badgesInFlightRef.current) {
      if (!force) return badgesInFlightRef.current;
      try {
        await badgesInFlightRef.current;
      } catch (error) {
        // ignore previous poll error and fetch again
      }
    }

    const request = (async () => {
      try {
        const data = await postWithAuth(
          currentUser,
          API_URLS.getAdminBadges,
          champFields(champIdRef.current)
        );
        const pending = applyPendingPayload(data);
        const pendingForms = applyPendingClientFormsPayload(data);
        const accessPatch = data && typeof data === 'object' && (
          data.admin_access
          || data.champ_access
          || data.roles
          || data.champs_ids
          || typeof data.unrestricted_champs === 'boolean'
        )
          ? {
            role: data.role,
            roles: data.roles,
            champs_ids: data.champs_ids,
            unrestricted_champs: data.unrestricted_champs,
            admin_access: data.admin_access,
            champ_access: data.champ_access,
          }
          : null;
        if (pending || pendingForms || accessPatch) {
          syncUserDefaultChamp(null, pending, pendingForms, accessPatch);
        }
        lastBadgesAtRef.current = Date.now();
        return data;
      } catch (error) {
        // keep the current badges
        return null;
      } finally {
        if (badgesInFlightRef.current === request) {
          badgesInFlightRef.current = null;
        }
      }
    })();

    badgesInFlightRef.current = request;
    return request;
  }, [applyPendingPayload, applyPendingClientFormsPayload, syncUserDefaultChamp]);

  const refreshPendingCapApplications = useCallback(
    () => refreshAdminBadges({ force: true }),
    [refreshAdminBadges]
  );
  const refreshPendingClientForms = useCallback(
    () => refreshAdminBadges({ force: true }),
    [refreshAdminBadges]
  );

  const reload = useCallback(() => {
    loadedForUserRef.current = null;
    setLoadNonce((value) => value + 1);
  }, []);

  const sessionUserKey = userData
    ? (userData.login || userData.id || userData.user_id || 'session')
    : null;

  useEffect(() => {
    if (!sessionUserKey) {
      lastBadgesAtRef.current = 0;
      return undefined;
    }

    refreshAdminBadges({ force: true });

    const timerId = window.setInterval(() => {
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
      refreshAdminBadges();
    }, ADMIN_BADGES_POLL_MS);

    const onFocusOrVisible = () => {
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
      refreshAdminBadges();
    };

    window.addEventListener('focus', onFocusOrVisible);
    document.addEventListener('visibilitychange', onFocusOrVisible);

    return () => {
      window.clearInterval(timerId);
      window.removeEventListener('focus', onFocusOrVisible);
      document.removeEventListener('visibilitychange', onFocusOrVisible);
    };
  }, [sessionUserKey, refreshAdminBadges]);

  useEffect(() => {
    if (!userData) {
      pendingSeededForRef.current = null;
      setPendingCap(EMPTY_PENDING_CAP_APPLICATIONS);
      setPendingClientForms(EMPTY_PENDING_CLIENT_FORMS);
      return;
    }
    const userKey = userData.login || userData.id || userData.user_id || 'session';
    if (pendingSeededForRef.current === userKey) return;
    pendingSeededForRef.current = userKey;
    const extracted = extractPendingCapApplications(userData);
    if (extracted) setPendingCap(extracted);
    const extractedForms = extractPendingClientForms(userData);
    if (extractedForms) setPendingClientForms(extractedForms);
  }, [userData]);

  useEffect(() => {
    if (!userData) {
      loadedForUserRef.current = null;
      setGroups([]);
      setError(null);
      setLoading(false);
      return undefined;
    }

    const userKey = userData.login || userData.id || userData.user_id || 'session';
    if (loadedForUserRef.current === userKey) {
      return undefined;
    }

    let cancelled = false;
    const stored = getStoredChampId();
    setChampIdState(stored);
    setLoading(true);
    setError(null);

    fetchGroupedChampionships(userData)
      .then((data) => {
        if (cancelled) return;
        // Read access is global: show every championship from the API in the selector.
        const nextGroups = normalizeGroups(data);
        const flat = flattenChampionships(nextGroups);
        const nextId = resolveChampId(flat, userData);
        loadedForUserRef.current = userKey;
        setGroups(nextGroups);
        if (nextId) {
          const hasServerDefault = Boolean(getDefaultChampIdFromUser(userData));
          setChampId(nextId, { persist: !hasServerDefault });
        } else {
          setChampIdState(null);
        }
      })
      .catch((err) => {
        if (cancelled) return;
        setGroups([]);
        setError(err);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [userData, setChampId, loadNonce]);

  const championships = useMemo(() => flattenChampionships(groups), [groups]);
  const selected = useMemo(
    () => championships.find((item) => item.id === champId) || null,
    [championships, champId]
  );
  const pendingCapApplicationsCount = useMemo(
    () => pendingCapCountForChamp(pendingCap, champId),
    [pendingCap, champId]
  );
  const pendingClientFormsCount = useMemo(
    () => pendingClientFormsCountForChamp(pendingClientForms, champId),
    [pendingClientForms, champId]
  );

  const value = useMemo(() => ({
    groups,
    championships,
    champId,
    selected,
    loading,
    error,
    pendingCapApplicationsCount,
    pendingClientFormsCount,
    setChampId,
    reload,
    refreshAdminBadges,
    refreshPendingCapApplications,
    refreshPendingClientForms,
  }), [
    groups,
    championships,
    champId,
    selected,
    loading,
    error,
    pendingCapApplicationsCount,
    pendingClientFormsCount,
    setChampId,
    reload,
    refreshAdminBadges,
    refreshPendingCapApplications,
    refreshPendingClientForms,
  ]);

  return (
    <ChampionshipContext.Provider value={value}>
      {children}
    </ChampionshipContext.Provider>
  );
}

export function useChampionships() {
  return useContext(ChampionshipContext);
}
